// Single source of truth for the text a readiness-card answer widget SENDS.
//
// Both the inline chat card (render/index.tsx) and the pinned editor card
// (IssuePage.tsx) route widget answers through these builders, so the two
// surfaces never drift and the text the agent must quote is canonical.
//
// GROUNDING DISCIPLINE: the gate (propose_card) is pure normalized-substring
// containment over ALL prior user turns with NO word boundaries, so a bare
// "High"/"5" would false-green off an unrelated earlier turn. Every short /
// enum answer is therefore SELF-LABELLED ("Priority: High"), which is what a
// widget option's `value` carries — long + field-specific kills both
// quote_not_found and misattribution. Free-text answers (role/means/ends) are
// full phrases the user types and carry their own distinctiveness. List answers
// (AC / DoD) go out as ONE newline-joined message because the gate never
// stitches a quote across turns.

import type { WidgetSpec } from "@loom/shared/readiness";

import type { MessageKey } from "../../i18n/messages/index";

// The list-answer format moved to `@loom/shared/readiness`: the SERVER composes
// it now too (a park re-writes the acceptance-criteria green from the rows the
// user kept, and those bytes must be identical to what this widget would have
// sent). Re-exported from here so every existing import site is unchanged and
// there stays exactly one definition.
export {
  AC_HEADING,
  DOD_HEADING,
  buildListAnswer,
  listHeadingForField,
} from "@loom/shared/readiness";

/** Priority choices. `value` is the labelled send-text == the agent's quote;
 *  `label` is the bare display token. Maps to apply_update_issue.priority
 *  (high/normal/low) which the agent derives from the label. */
export const PRIORITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "Priority: High", label: "High" },
  { value: "Priority: Normal", label: "Normal" },
  { value: "Priority: Low", label: "Low" },
];

/**
 * The DISPLAY label for a closed-option value, as a catalog key.
 *
 * The two planes of an option split here and must never be conflated:
 *
 *  - `value` is the GROUNDING plane. It is the text the widget sends, the
 *    bytes the user's turn carries, and the string the agent quotes back past
 *    the containment gate. Byte-exact English by contract — localizing it would
 *    break the quote scan.
 *  - the LABEL is chrome. It is read, never quoted, and it follows the UI
 *    locale like every other word on the screen.
 *
 * Live, three buttons reading "High" / "Normal" / "Low" sat under a German
 * question ("Wie dringend ist diese Verbesserung …?") in a session where every
 * other string was German. The keys already existed (`priority.*`, used by the
 * dashboard's SteerRow); nothing was routing the widget's labels through them.
 *
 * KNOWN GAP, and it is the reported session: the caller resolves this key with
 * `useT()`, which reads the OPERATOR's UI locale (`browserDefault()` →
 * `navigator.language`, or the stored override). The question above the buttons
 * is written at the PROJECT's content locale, which the server owns
 * (`session.config.locale`) and does not send to the browser — no frame and no
 * endpoint carries it (`GET /sessions/:id` and `readiness_card_pending` both
 * omit it). So an `en` browser on a `de` project still reads "High / Normal /
 * Low" under a German question: this makes the labels follow the chrome, which
 * is the most a client with no content locale can do. Closing it needs the
 * locale on the wire first, then a content-locale translator here — NOT a
 * second UI locale, and never on the option's `value`.
 *
 * Returns null for an option this map does not own (a milestone title, an
 * agent-supplied option) — those carry their own text and are shown verbatim.
 */
export const optionLabelKey = (value: string): MessageKey | null => {
  switch (value) {
    case "Priority: High":
      return "priority.high";
    case "Priority: Normal":
      return "priority.normal";
    case "Priority: Low":
      return "priority.low";
    default:
      return null;
  }
};

/**
 * The option `value` for a GitLab-derived priority, so a caller holding the
 * ISSUE's current priority (`DashboardIssue.priority`, derived from the
 * `priority::pN` label) can mark the option it corresponds to.
 *
 * Deliberately maps to the option's `value` and not its `label`: the label is
 * chrome and is localized (`optionLabelKey`), so matching on it would stop
 * matching the moment the UI locale changes.
 */
export const priorityOptionValue = (
  priority: "high" | "normal" | "low",
): string =>
  priority === "high"
    ? "Priority: High"
    : priority === "low"
      ? "Priority: Low"
      : "Priority: Normal";

/** `Milestone: <title>` — the user-quotable title (NOT the id; the agent
 *  resolves the id via list_milestones for apply_update_issue.milestoneId). */
export const buildMilestoneAnswer = (title: string): string =>
  `Milestone: ${title}`;

/**
 * The widget the renderer uses when the agent did not attach one — every card
 * field has a sensible default so the user always gets a purpose-built control.
 * story_points has no widget: it is AI-estimated (estimate_story_points), so a
 * needs_input on it falls back to plain text for the rare manual override.
 */
export const defaultWidgetForField = (field: string): WidgetSpec => {
  switch (field) {
    case "priority":
      return { type: "single_select", options: [...PRIORITY_OPTIONS] };
    case "milestone":
      return { type: "dynamic_select", source: "milestone" };
    case "acceptance_criteria":
    case "dod_criteria":
      return { type: "list_builder" };
    default:
      return { type: "text" };
  }
};

